import { useEffect, useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  CalendarDays,
} from "lucide-react";

import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Badge } from "../../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";

const STORAGE_KEY = "aivideo_calendar_entries_v1";

const PLATFORMS = [
  { key: "tiktok", label: "TikTok", color: "bg-pink-500" },
  { key: "instagram", label: "Instagram", color: "bg-purple-500" },
  { key: "facebook", label: "Facebook", color: "bg-blue-500" },
  { key: "youtube", label: "YouTube", color: "bg-red-500" },
  { key: "other", label: "Anders", color: "bg-gray-500" },
];

const STATUSES = [
  { key: "idea", label: "Idee", color: "bg-gray-400" },
  { key: "planned", label: "Gepland", color: "bg-blue-400" },
  { key: "recorded", label: "Opgenomen", color: "bg-yellow-400" },
  { key: "published", label: "Gepubliceerd", color: "bg-emerald-500" },
];

const DAYS_NL = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];

const MONTHS_NL = [
  "januari", "februari", "maart", "april", "mei", "juni",
  "juli", "augustus", "september", "oktober", "november", "december",
];

const isoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const loadEntries = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_e) {
    return [];
  }
};

const saveEntries = (entries) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch (_e) {
    // ignore quota
  }
};

const platformMeta = (key) => PLATFORMS.find((p) => p.key === key) || PLATFORMS[4];
const statusMeta = (key) => STATUSES.find((s) => s.key === key) || STATUSES[0];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
      {label}
    </span>
    {children}
  </label>
);

const CalendarTab = ({ isDark }) => {
  const [entries, setEntries] = useState(loadEntries);
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [editing, setEditing] = useState(null); // { id?, date, title, platform, status }
  const [pendingDelete, setPendingDelete] = useState(null);

  useEffect(() => {
    saveEntries(entries);
  }, [entries]);

  const monthLabel = `${MONTHS_NL[cursor.getMonth()]} ${cursor.getFullYear()}`;

  const grid = useMemo(() => {
    const firstOfMonth = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    // Monday-first weekday: JS getDay() Sun=0..Sat=6 → Mon=0..Sun=6
    const startDow = (firstOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(
      cursor.getFullYear(),
      cursor.getMonth() + 1,
      0
    ).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const entriesByDate = useMemo(() => {
    const map = new Map();
    entries.forEach((e) => {
      if (!map.has(e.date)) map.set(e.date, []);
      map.get(e.date).push(e);
    });
    return map;
  }, [entries]);

  const openNew = (date) => {
    setEditing({
      id: null,
      date: isoDate(date),
      title: "",
      platform: "tiktok",
      status: "idea",
    });
  };

  const openEdit = (entry) => setEditing({ ...entry });

  const save = () => {
    if (!editing || !editing.title.trim()) return;
    setEntries((prev) => {
      if (editing.id) {
        return prev.map((e) => (e.id === editing.id ? { ...editing } : e));
      }
      return [
        ...prev,
        {
          ...editing,
          id: `cal_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        },
      ];
    });
    setEditing(null);
  };

  const remove = (id) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
    setPendingDelete(null);
    if (editing && editing.id === id) setEditing(null);
  };

  const prevMonth = () =>
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  const nextMonth = () =>
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  const today = () => {
    const d = new Date();
    d.setDate(1);
    setCursor(d);
  };

  const todayIso = isoDate(new Date());

  return (
    <div
      className={cn(
        "rounded-xl border",
        isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-white border-gray-200/70"
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between px-4 py-3 border-b",
          isDark ? "border-white/[0.06]" : "border-gray-100"
        )}
      >
        <div className="flex items-center gap-2">
          <CalendarDays
            className={cn("h-4 w-4", isDark ? "text-indigo-400" : "text-indigo-500")}
          />
          <h2 className={cn("text-[14px] font-semibold capitalize", isDark ? "text-white" : "text-gray-900")}>
            {monthLabel}
          </h2>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={prevMonth} className="h-7 px-2">
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <Button variant="outline" size="sm" onClick={today} className="h-7 text-[11px]">
            Vandaag
          </Button>
          <Button variant="ghost" size="sm" onClick={nextMonth} className="h-7 px-2">
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div
        className={cn(
          "grid grid-cols-7 border-b",
          isDark ? "border-white/[0.06]" : "border-gray-100"
        )}
      >
        {DAYS_NL.map((d) => (
          <div
            key={d}
            className={cn(
              "text-center text-[10px] uppercase tracking-wider font-semibold py-2",
              isDark ? "text-gray-500" : "text-gray-400"
            )}
          >
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {grid.map((d, i) => {
          if (!d) {
            return (
              <div
                key={`empty_${i}`}
                className={cn(
                  "min-h-[88px] border-r border-b",
                  isDark ? "border-white/[0.04] bg-white/[0.01]" : "border-gray-100 bg-gray-50/30"
                )}
              />
            );
          }
          const iso = isoDate(d);
          const dayEntries = entriesByDate.get(iso) || [];
          const isToday = iso === todayIso;
          return (
            <div
              key={iso}
              onClick={() => openNew(d)}
              className={cn(
                "min-h-[88px] border-r border-b p-1.5 cursor-pointer transition-colors flex flex-col",
                isDark
                  ? "border-white/[0.06] hover:bg-white/[0.03]"
                  : "border-gray-100 hover:bg-gray-50",
                isToday && (isDark ? "bg-indigo-500/[0.05]" : "bg-indigo-50/40")
              )}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className={cn(
                    "text-[11px] font-semibold",
                    isToday
                      ? isDark ? "text-indigo-300" : "text-indigo-700"
                      : isDark ? "text-gray-400" : "text-gray-600"
                  )}
                >
                  {d.getDate()}
                </span>
                {dayEntries.length === 0 && (
                  <Plus
                    className={cn(
                      "h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity",
                      isDark ? "text-gray-500" : "text-gray-400"
                    )}
                  />
                )}
              </div>
              <div className="flex flex-col gap-0.5 flex-1">
                {dayEntries.slice(0, 3).map((e) => {
                  const pm = platformMeta(e.platform);
                  const sm = statusMeta(e.status);
                  return (
                    <button
                      key={e.id}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        openEdit(e);
                      }}
                      className={cn(
                        "text-left rounded px-1 py-0.5 text-[10px] flex items-center gap-1 truncate border",
                        isDark
                          ? "bg-white/[0.05] border-white/[0.08] text-gray-200 hover:bg-white/[0.10]"
                          : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                      )}
                    >
                      <span className={cn("h-1.5 w-1.5 rounded-full flex-shrink-0", pm.color)} />
                      <span className={cn("h-1.5 w-1.5 rounded-full flex-shrink-0", sm.color)} />
                      <span className="truncate">{e.title}</span>
                    </button>
                  );
                })}
                {dayEntries.length > 3 && (
                  <span
                    className={cn(
                      "text-[10px]",
                      isDark ? "text-gray-500" : "text-gray-400"
                    )}
                  >
                    +{dayEntries.length - 3} meer
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div
        className={cn(
          "flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2 border-t",
          isDark ? "border-white/[0.06]" : "border-gray-100"
        )}
      >
        <span
          className={cn(
            "text-[10px] uppercase tracking-wider font-semibold",
            isDark ? "text-gray-500" : "text-gray-400"
          )}
        >
          Status
        </span>
        {STATUSES.map((s) => (
          <span
            key={s.key}
            className={cn(
              "flex items-center gap-1 text-[11px]",
              isDark ? "text-gray-400" : "text-gray-600"
            )}
          >
            <span className={cn("h-2 w-2 rounded-full", s.color)} />
            {s.label}
          </span>
        ))}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editing?.id ? "Concept bewerken" : "Nieuw concept"}
            </DialogTitle>
            <DialogDescription>
              {editing?.date && `Datum: ${editing.date}`}
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-3">
              <Field label="Titel">
                <Input
                  value={editing.title}
                  onChange={(e) =>
                    setEditing((s) => ({ ...s, title: e.target.value }))
                  }
                  placeholder="bv. 'Voor en na transformatie'"
                  autoFocus
                />
              </Field>

              <Field label="Datum">
                <Input
                  type="date"
                  value={editing.date}
                  onChange={(e) =>
                    setEditing((s) => ({ ...s, date: e.target.value }))
                  }
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Platform">
                  <Select
                    value={editing.platform}
                    onValueChange={(v) =>
                      setEditing((s) => ({ ...s, platform: v }))
                    }
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PLATFORMS.map((p) => (
                        <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label="Status">
                  <Select
                    value={editing.status}
                    onValueChange={(v) =>
                      setEditing((s) => ({ ...s, status: v }))
                    }
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="text-[10px] h-[18px]">
                  <span className={cn("h-1.5 w-1.5 rounded-full mr-1", platformMeta(editing.platform).color)} />
                  {platformMeta(editing.platform).label}
                </Badge>
                <Badge variant="outline" className="text-[10px] h-[18px]">
                  <span className={cn("h-1.5 w-1.5 rounded-full mr-1", statusMeta(editing.status).color)} />
                  {statusMeta(editing.status).label}
                </Badge>
              </div>
            </div>
          )}

          <DialogFooter className="flex items-center justify-between sm:justify-between">
            {editing?.id ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPendingDelete(editing.id)}
                className="text-red-500 hover:text-red-600 gap-1"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Verwijderen
              </Button>
            ) : <span />}
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>
                Annuleren
              </Button>
              <Button onClick={save} disabled={!editing?.title?.trim()}>
                Opslaan
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Concept verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Deze actie kan niet ongedaan gemaakt worden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove(pendingDelete)}>
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CalendarTab;
